import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatRupeeFixed, formatRupeeInt } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Rent',
  'Staff Salary',
  'Cleaning Staff Salary',
  'Transportation',
  'Power Bill',
  'Internet Bill',
  'Other'
];

const categoryColors = {
  'Rent': 'bg-violet-100 text-violet-800',
  'Staff Salary': 'bg-blue-100 text-blue-800',
  'Cleaning Staff Salary': 'bg-cyan-100 text-cyan-800',
  'Transportation': 'bg-amber-100 text-amber-800',
  'Power Bill': 'bg-orange-100 text-orange-800',
  'Internet Bill': 'bg-teal-100 text-teal-800',
  'Other': 'bg-stone-100 text-stone-800'
};

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => { fetchExpenses(); }, []);

  const fetchExpenses = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/expenses`, { withCredentials: true });
      setExpenses(res.data);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category || !formData.amount) {
      toast.error('Category and amount are required');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/expenses`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, { withCredentials: true });
      toast.success('Expense recorded');
      setDialogOpen(false);
      setFormData({ category: '', description: '', amount: '', date: new Date().toISOString().split('T')[0] });
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record expense');
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/expenses/${expenseId}`, { withCredentials: true });
      toast.success('Expense deleted');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const filtered = categoryFilter === 'ALL' ? expenses : expenses.filter(e => e.category === categoryFilter);

  // Monthly summary
  const monthlySummary = {};
  expenses.forEach(e => {
    const month = e.date?.substring(0, 7) || 'Unknown';
    if (!monthlySummary[month]) monthlySummary[month] = 0;
    monthlySummary[month] += e.amount;
  });
  const monthlyEntries = Object.entries(monthlySummary).sort((a, b) => b[0].localeCompare(a[0]));

  const totalExpenses = filtered.reduce((sum, e) => sum + e.amount, 0);

  if (loading) return <div className="text-stone-600">Loading expenses...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Expenses</h1>
          <p className="mt-1 text-xs text-stone-600">{expenses.length} entries &middot; Total: {formatRupeeFixed(totalExpenses)}</p>
        </div>
        <Button data-testid="add-expense-btn" onClick={() => setDialogOpen(true)} size="sm" className="bg-amber-700 hover:bg-amber-800 text-white text-xs h-8">
          <Plus className="w-3 h-3 mr-1" /> Record Expense
        </Button>
      </div>

      {/* Monthly Summary Cards */}
      {monthlyEntries.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {monthlyEntries.slice(0, 6).map(([month, total]) => (
            <div key={month} className="bg-white border border-stone-200 rounded-lg p-3">
              <div className="text-xs text-stone-500 font-medium">{month}</div>
              <div className="text-lg font-bold text-stone-900 tabular-nums">{formatRupeeInt(total)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 items-center">
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger data-testid="expense-category-filter" className="w-48 border-stone-200 text-xs h-8">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL" className="text-xs">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-stone-100 border-b border-stone-300">
            <tr>
              <th className="text-left p-2.5 font-semibold text-stone-700">DATE</th>
              <th className="text-left p-2.5 font-semibold text-stone-700">CATEGORY</th>
              <th className="text-left p-2.5 font-semibold text-stone-700">DESCRIPTION</th>
              <th className="text-right p-2.5 font-semibold text-stone-700">AMOUNT</th>
              <th className="text-right p-2.5 font-semibold text-stone-700 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="5" className="p-8 text-center text-stone-500">No expenses recorded yet.</td></tr>
            ) : (
              filtered.map((exp, idx) => (
                <tr key={exp.expense_id} data-testid={`expense-row-${exp.expense_id}`} className={`border-b border-stone-100 hover:bg-stone-50 ${idx % 2 === 0 ? '' : 'bg-stone-50/50'}`}>
                  <td className="p-2.5 tabular-nums text-stone-700">{exp.date}</td>
                  <td className="p-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${categoryColors[exp.category] || categoryColors['Other']}`}>{exp.category}</span>
                  </td>
                  <td className="p-2.5 text-stone-700">{exp.description || '-'}</td>
                  <td className="p-2.5 text-right font-semibold text-stone-900 tabular-nums">{formatRupeeFixed(exp.amount)}</td>
                  <td className="p-2.5 text-right">
                    <button data-testid={`delete-expense-${exp.expense_id}`} onClick={() => handleDelete(exp.expense_id)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Record Expense</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger data-testid="expense-category-select" className="border-stone-200"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount *</Label>
              <Input data-testid="expense-amount-input" type="number" step="0.01" min="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" required className="border-stone-200" />
            </div>
            <div>
              <Label>Date *</Label>
              <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="border-stone-200" />
            </div>
            <div>
              <Label>Description</Label>
              <Input data-testid="expense-desc-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Optional notes" className="border-stone-200" />
            </div>
            <Button type="submit" data-testid="submit-expense-btn" className="w-full bg-amber-700 hover:bg-amber-800 text-white">Record Expense</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;
